import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';

const STORAGE_KEY = 'sjmate_cookie_consent';
const EVENT_OPEN_SETTINGS = 'sjmate-open-cookie-settings';

export interface CookieConsentState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  consentedAt: string;
}

function loadConsent(): CookieConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      necessary: parsed.necessary !== false,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      consentedAt: parsed.consentedAt || new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

function saveConsent(state: CookieConsentState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, consentedAt: new Date().toISOString() }));
}

const Banner = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #1a1a1a;
  color: #fff;
  padding: 1rem 1.5rem;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  font-family: 'Poppins', sans-serif;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: 1rem;
  }
`;

const BannerText = styled.p`
  flex: 1;
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
  min-width: 200px;
  color: #fff;
  a {
    color: #ffb366;
    text-decoration: underline;
  }
  button {
    color: #ffb366;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const BannerButton = styled.button<{ $primary?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  border: 2px solid ${(p) => (p.$primary ? 'var(--accent-orange)' : '#555')};
  background: ${(p) => (p.$primary ? 'var(--accent-orange)' : 'transparent')};
  color: ${(p) => (p.$primary ? '#fff' : '#fff')};
  transition: all 0.2s ease;
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 0.75rem;
  max-width: 440px;
  width: 100%;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  font-family: 'Poppins', sans-serif;
`;

const ModalTitle = styled.h2`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  color: var(--text-gray);
`;

const ModalParagraph = styled.p`
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
  color: var(--text-gray);
  line-height: 1.4;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #eee;
  &:last-of-type {
    border-bottom: none;
  }
`;

const RowLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-gray);
`;

const Toggle = styled.input.attrs({ type: 'checkbox' })`
  width: 2.5rem;
  height: 1.25rem;
  accent-color: var(--accent-orange);
  cursor: pointer;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1.25rem;
  justify-content: flex-end;
`;

const ModalCancelButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  border: 2px solid #ddd;
  background: #fff;
  color: #1a1a1a;
  transition: all 0.2s ease;
  &:hover {
    border-color: #999;
    background: #f5f5f5;
  }
`;

const ModalSaveButton = styled(BannerButton)``;

const CookieConsent: React.FC = () => {
  const { token, refreshProfile } = useAuth();
  const [consent, setConsentState] = useState<CookieConsentState | null>(() => loadConsent());
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalPrefs, setModalPrefs] = useState<CookieConsentState>({
    necessary: true,
    analytics: false,
    marketing: false,
    consentedAt: new Date().toISOString(),
  });

  useEffect(() => {
    setShowBanner(consent === null);
  }, [consent]);

  useEffect(() => {
    const open = () => setShowModal(true);
    window.addEventListener(EVENT_OPEN_SETTINGS, open);
    return () => window.removeEventListener(EVENT_OPEN_SETTINGS, open);
  }, []);

  const syncToBackend = useCallback(
    async (prefs: CookieConsentState) => {
      const t = token ?? localStorage.getItem('token');
      if (!t) return;
      try {
        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/auth/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${t}`,
          },
          body: JSON.stringify({
            cookiePreferences: {
              necessary: prefs.necessary,
              analytics: prefs.analytics,
              marketing: prefs.marketing,
            },
          }),
        });
        refreshProfile();
      } catch (e) {
        console.error('Failed to sync cookie preferences to server', e);
      }
    },
    [token, refreshProfile]
  );

  const applyConsent = useCallback(
    (state: CookieConsentState) => {
      saveConsent(state);
      setConsentState(state);
      setShowBanner(false);
      setShowModal(false);
      syncToBackend(state);
    },
    [syncToBackend]
  );

  const handleAcceptAll = () => {
    applyConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      consentedAt: new Date().toISOString(),
    });
  };

  const handleEssentialOnly = () => {
    applyConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      consentedAt: new Date().toISOString(),
    });
  };

  const handleOpenSettings = () => {
    setModalPrefs(
      consent ?? {
        necessary: true,
        analytics: false,
        marketing: false,
        consentedAt: new Date().toISOString(),
      }
    );
    setShowModal(true);
  };

  const handleSaveSettings = () => {
    applyConsent(modalPrefs);
  };

  return (
    <>
      {showBanner && (
        <Banner role="dialog" aria-label="Cookie consent">
          <BannerText>
            We use cookies to provide the service, analyze usage, and improve your experience. See our{' '}
            <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
            <button type="button" onClick={handleOpenSettings} style={{ background: 'none', border: 'none', color: 'var(--accent-orange)', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>
              Cookie settings
            </button>.
          </BannerText>
          <ButtonGroup>
            <BannerButton type="button" onClick={handleEssentialOnly}>
              Essential only
            </BannerButton>
            <BannerButton type="button" onClick={handleAcceptAll} $primary>
              Accept all
            </BannerButton>
            <BannerButton type="button" onClick={handleOpenSettings}>
              Customize
            </BannerButton>
          </ButtonGroup>
        </Banner>
      )}

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Cookie settings</ModalTitle>
            <ModalParagraph>
              Choose which cookies you allow. Necessary cookies are required for the app to work.
            </ModalParagraph>
            <Row>
              <RowLabel>Necessary</RowLabel>
              <Toggle checked disabled />
            </Row>
            <Row>
              <RowLabel>Analytics</RowLabel>
              <Toggle
                checked={modalPrefs.analytics}
                onChange={(e) => setModalPrefs((p) => ({ ...p, analytics: e.target.checked }))}
              />
            </Row>
            <Row>
              <RowLabel>Marketing</RowLabel>
              <Toggle
                checked={modalPrefs.marketing}
                onChange={(e) => setModalPrefs((p) => ({ ...p, marketing: e.target.checked }))}
              />
            </Row>
            <ModalButtons>
              <ModalCancelButton type="button" onClick={() => setShowModal(false)}>
                Cancel
              </ModalCancelButton>
              <ModalSaveButton type="button" onClick={handleSaveSettings} $primary>
                Save
              </ModalSaveButton>
            </ModalButtons>
          </Modal>
        </Overlay>
      )}
    </>
  );
};

export default CookieConsent;

export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent(EVENT_OPEN_SETTINGS));
}
